import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Leaf, ArrowLeft, ArrowRight, Home, User, Wrench, Euro, CheckCircle, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import StepNavigation from "@/components/StepNavigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Style commun pour les selects natifs
const selectStyle = {
  color: '#1D1D1D',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10px center',
  backgroundSize: '16px',
  paddingRight: '40px'
};

const STEPS = [
  { id: 1, title: "Informations personnelles", icon: User },
  { id: 2, title: "Votre logement", icon: Home },
  { id: 3, title: "Vos travaux", icon: Wrench },
  { id: 4, title: "Situation financière", icon: Euro },
  { id: 5, title: "Récapitulatif", icon: CheckCircle },
];

// Catégories de travaux avec icônes pour une meilleure UX
// NOTE: La catégorie "adaptation" (MaPrimeAdapt') est gérée séparément à l'étape 4
const WORK_CATEGORIES = [
  {
    id: "isolation",
    title: "🏠 Isolation",
    description: "Réduisez vos pertes de chaleur",
    works: [
      { id: "isolation_combles", label: "Isolation des combles" },
      { id: "isolation_murs", label: "Isolation des murs" },
      { id: "isolation_planchers", label: "Isolation des planchers" },
    ]
  },
  {
    id: "chauffage",
    title: "🔥 Chauffage & Eau chaude",
    description: "Modernisez votre système de chauffage",
    works: [
      { id: "pompe_chaleur", label: "Pompe à chaleur" },
      { id: "chaudiere", label: "Chaudière performante" },
      { id: "chauffe_eau_solaire", label: "Chauffe-eau solaire" },
      { id: "chauffe_eau_thermodynamique", label: "Chauffe-eau thermodynamique" },
    ]
  },
  {
    id: "ventilation_menuiseries",
    title: "💨 Ventilation & Menuiseries",
    description: "Améliorez la qualité de l'air",
    works: [
      { id: "ventilation", label: "Ventilation (VMC)" },
      { id: "fenetres", label: "Fenêtres / Menuiseries" },
    ]
  },
  {
    id: "autres",
    title: "📋 Autres travaux",
    description: "Audit et rénovation globale",
    works: [
      { id: "audit_energetique", label: "Audit énergétique" },
      { id: "renovation_globale", label: "Rénovation globale" },
    ]
  }
];

// Liste plate pour compatibilité (utilisée dans le récapitulatif)
// Inclut aussi les travaux d'adaptation qui sont gérés séparément
const WORK_TYPES = [
  ...WORK_CATEGORIES.flatMap(cat => cat.works),
  // Travaux d'adaptation (MaPrimeAdapt') - ajoutés manuellement car gérés à l'étape 4
  { id: "douche_adaptee", label: "Douche adaptée (remplacement baignoire)" },
  { id: "monte_escalier", label: "Monte-escalier / Élévateur" },
  { id: "elargissement_portes", label: "Élargissement des portes" },
  { id: "rampes", label: "Rampes et mains courantes" },
  { id: "adaptation_salle_de_bain", label: "Adaptation salle de bain complète" },
  { id: "wc_rehausse", label: "WC rehaussé ou adapté" },
  { id: "volets_motorises", label: "Volets roulants motorisés" },
];

// Types de travaux d'adaptation (MaPrimeAdapt') - gardé pour compatibilité
const ADAPTATION_WORK_TYPES = [
  { id: "douche_adaptee", label: "Douche adaptée (remplacement baignoire)" },
  { id: "monte_escalier", label: "Monte-escalier / Élévateur" },
  { id: "elargissement_portes", label: "Élargissement des portes" },
  { id: "rampes", label: "Rampes et mains courantes" },
  { id: "adaptation_salle_de_bain", label: "Adaptation salle de bain complète" },
  { id: "autres", label: "Autres travaux d'adaptation" },
];

const DEPARTMENTS = [
  "01", "02", "03", "04", "05", "06", "07", "08", "09", "10",
  "11", "12", "13", "14", "15", "16", "17", "18", "19", "21",
  "22", "23", "24", "25", "26", "27", "28", "29", "2A", "2B",
  "30", "31", "32", "33", "34", "35", "36", "37", "38", "39",
  "40", "41", "42", "43", "44", "45", "46", "47", "48", "49",
  "50", "51", "52", "53", "54", "55", "56", "57", "58", "59",
  "60", "61", "62", "63", "64", "65", "66", "67", "68", "69",
  "70", "71", "72", "73", "74", "75", "76", "77", "78", "79",
  "80", "81", "82", "83", "84", "85", "86", "87", "88", "89",
  "90", "91", "92", "93", "94", "95"
];

export default function ParticulierForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editLeadId = searchParams.get('edit');
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    // Personal Info
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    // Address
    address: "",
    postal_code: "",
    city: "",
    department: "",
    // Housing
    housing_type: "maison",
    residence_type: "principale", // principale ou secondaire - crucial pour éligibilité
    housing_status: "proprietaire",
    housing_age: "",
    housing_surface: "",
    // Work Types
    work_types: [],
    // Financial
    // Financial Info - Normalized
    income_rfr: "",
    household_size: "1",
    // Urgency
    urgency: "3_mois",
    additional_info: "",
    // MaPrimeAdapt' fields
    age: "",
    situation_handicap: false,
    perte_autonomie: false,
    gir_level: "",
    adaptation_works: [],
    // Loc'Avantages fields (propriétaire bailleur)
    owner_type: "personne_physique",
    conventionnement_level: "",
    with_intermediation: false,
    rental_duration_years: 6,
    current_rent: "",
    market_rent: "",
    zone_tendue: "",
    has_travaux_renovation: false,
  });

  // Charger les données existantes si on est en mode édition
  useEffect(() => {
    if (editLeadId) {
      const loadLeadData = async () => {
        try {
          const response = await axios.get(`${API}/leads/${editLeadId}`);
          const lead = response.data;
          setFormData(prev => ({
            ...prev,
            first_name: lead.first_name || "",
            last_name: lead.last_name || "",
            email: lead.email || "",
            phone: lead.phone || "",
            address: lead.address || "",
            postal_code: lead.postal_code || "",
            city: lead.city || "",
            department: lead.department || "",
            housing_type: lead.housing_type || "maison",
            residence_type: lead.residence_type || "principale",
            housing_status: lead.housing_status || "proprietaire",
            housing_age: lead.housing_age?.toString() || "",
            housing_surface: lead.housing_surface?.toString() || "",
            work_types: lead.work_types || [],
            income_rfr: lead.income_rfr?.toString() || "",
            household_size: lead.household_size?.toString() || "1",
            urgency: lead.urgency || "3_mois",
            additional_info: lead.additional_info || "",
            age: lead.age?.toString() || "",
            situation_handicap: lead.situation_handicap || false,
            perte_autonomie: lead.perte_autonomie || false,
            gir_level: lead.gir_level || "",
            adaptation_works: lead.adaptation_works || [],
            owner_type: lead.owner_type || "personne_physique",
            conventionnement_level: lead.conventionnement_level || "",
            with_intermediation: lead.with_intermediation || false,
            rental_duration_years: lead.rental_duration_years || 6,
            current_rent: lead.current_rent?.toString() || "",
            market_rent: lead.market_rent?.toString() || "",
            zone_tendue: lead.zone_tendue || "",
            has_travaux_renovation: lead.has_travaux_renovation || false,
          }));
          setIsEditMode(true);
          toast.info("Vos informations ont été rechargées. Modifiez et soumettez à nouveau.");
        } catch (error) {
          console.error("Erreur chargement lead:", error);
          toast.error("Impossible de charger vos informations");
        }
      };
      loadLeadData();
    }
  }, [editLeadId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleWorkTypeToggle = (workId) => {
    setFormData(prev => {
      const currentTypes = prev.work_types;
      if (currentTypes.includes(workId)) {
        return { ...prev, work_types: currentTypes.filter(t => t !== workId) };
      } else {
        return { ...prev, work_types: [...currentTypes, workId] };
      }
    });
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
          toast.error("Veuillez remplir tous les champs obligatoires");
          return false;
        }
        if (!/\S+@\S+\.\S+/.test(formData.email)) {
          toast.error("Veuillez entrer un email valide");
          return false;
        }
        return true;
      case 2:
        if (!formData.address || !formData.postal_code || !formData.city || !formData.department) {
          toast.error("Veuillez remplir toutes les informations de votre logement");
          return false;
        }
        if (!formData.housing_age || !formData.housing_surface) {
          toast.error("Veuillez indiquer l'âge et la surface de votre logement");
          return false;
        }
        return true;
      case 3:
        if (formData.work_types.length === 0) {
          toast.error("Veuillez sélectionner au moins un type de travaux");
          return false;
        }
        return true;
      case 4:
        if (!formData.income_rfr) {
          toast.error("Veuillez indiquer votre revenu fiscal de référence");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  // Fonction pour obtenir le libellé du statut d'occupation
  const getHousingStatusLabel = (status) => {
    const labels = {
      "proprietaire": "Propriétaire occupant",
      "proprietaire_bailleur": "Propriétaire bailleur privé",
      "bailleur_social": "Bailleur social",
      "locataire": "Locataire parc privé",
      "locataire_social": "Locataire parc social",
      "usufruitier": "Usufruitier"
    };
    return labels[status] || status;
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        beneficiary_type: "particulier",
        housing_age: parseInt(formData.housing_age),
        housing_surface: parseInt(formData.housing_surface),
        income_rfr: parseInt(formData.income_rfr),
        household_size: parseInt(formData.household_size),
        // MaPrimeAdapt' fields - conversion explicite
        age: formData.age ? parseInt(formData.age) : null,
        situation_handicap: Boolean(formData.situation_handicap),
        perte_autonomie: Boolean(formData.perte_autonomie),
        gir_level: formData.gir_level && formData.gir_level !== "inconnu" ? parseInt(formData.gir_level) : null,
        adaptation_works: formData.adaptation_works.length > 0 ? formData.adaptation_works : null,
        // Loc'Avantages fields (propriétaire bailleur uniquement)
        owner_type: formData.housing_status === "proprietaire_bailleur" ? formData.owner_type : null,
        conventionnement_level: formData.housing_status === "proprietaire_bailleur" ? formData.conventionnement_level || null : null,
        with_intermediation: formData.housing_status === "proprietaire_bailleur" ? formData.with_intermediation : false,
        rental_duration_years: formData.housing_status === "proprietaire_bailleur" ? formData.rental_duration_years : null,
        current_rent: formData.current_rent ? parseInt(formData.current_rent) : null,
        market_rent: formData.market_rent ? parseInt(formData.market_rent) : null,
        zone_tendue: formData.housing_status === "proprietaire_bailleur" ? formData.zone_tendue || null : null,
        has_travaux_renovation: formData.housing_status === "proprietaire_bailleur" ? formData.has_travaux_renovation : false,
      };
      
      const response = await axios.post(`${API}/leads`, submitData);
      toast.success("Votre demande a été enregistrée !");
      navigate(`/resultat/${response.data.id}`);
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de l'envoi";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const progress = (currentStep / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-home">
            <Leaf className="h-7 w-7 text-[#1A4D2E]" />
            <span className="text-xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              MesSubventions
            </span>
          </Link>
          <Link 
            to="/"
            className="text-slate-500 hover:text-[#1A4D2E] text-sm flex items-center gap-1 transition-colors"
            data-testid="exit-form"
          >
            <ArrowLeft className="h-4 w-4" />
            Quitter
          </Link>
        </div>
      </header>

      {/* Progress Bar - Navigation cliquable */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <StepNavigation
            steps={STEPS}
            currentStep={currentStep}
            onStepClick={(step) => setCurrentStep(step)}
            className="mb-4"
          />
          <Progress value={progress} className="h-2" />
          <p className="text-sm text-slate-500 mt-2 text-center">
            Étape {currentStep} sur {STEPS.length}: {STEPS[currentStep - 1].title}
            {currentStep > 1 && (
              <span className="text-[#1A4D2E] ml-2">(cliquez sur une étape précédente pour y revenir)</span>
            )}
          </p>
        </div>
      </div>

      {/* Form Content - Enveloppé dans <form> pour l'autocomplétion navigateur */}
      <main className="max-w-2xl mx-auto px-4 py-12">
        {/* Bandeau mode édition */}
        {isEditMode && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-full">
              <ArrowLeft className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-blue-800">Mode modification</p>
              <p className="text-sm text-blue-600">Corrigez vos informations puis soumettez à nouveau pour recalculer votre éligibilité.</p>
            </div>
          </div>
        )}
        
        <form 
          autoComplete="on"
          method="post"
          className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 animate-fade-in"
          onSubmit={(e) => e.preventDefault()}
        >
          
          {/* Step 1: Personal Info */}
          {currentStep === 1 && (
            <div className="space-y-6" data-testid="step-1">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-[#1D1D1D]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Vos informations personnelles
                </h2>
                <p className="text-slate-500 mt-2">
                  Ces informations nous permettront de vous contacter
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fname">Prénom *</Label>
                  <input
                    id="fname"
                    name="fname"
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                    placeholder="Jean"
                    className="flex h-12 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]"
                    autoComplete="given-name"
                    data-testid="input-firstname"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lname">Nom *</Label>
                  <input
                    id="lname"
                    name="lname"
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                    placeholder="Dupont"
                    className="flex h-12 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]"
                    autoComplete="family-name"
                    data-testid="input-lastname"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="jean.dupont@email.com"
                  className="flex h-12 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]"
                  autoComplete="email"
                  data-testid="input-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone *</Label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="06 12 34 56 78"
                  className="flex h-12 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]"
                  autoComplete="tel"
                  data-testid="input-phone"
                />
              </div>
            </div>
          )}

          {/* Step 2: Housing Info */}
          {currentStep === 2 && (
            <div className="space-y-6" data-testid="step-2">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-[#1D1D1D]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Votre logement
                </h2>
                <p className="text-slate-500 mt-2">
                  Décrivez le logement concerné par les travaux
                </p>
              </div>

              <div className="space-y-2">
                <Label>Adresse *</Label>
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(value) => setFormData(prev => ({ ...prev, address: value }))}
                  onSelect={(addressData) => {
                    setFormData(prev => ({
                      ...prev,
                      address: addressData.address,
                      postal_code: addressData.postal_code,
                      city: addressData.city,
                      department: addressData.department
                    }));
                  }}
                  placeholder="Commencez à taper votre adresse..."
                  data-testid="input-address"
                />
                <p className="text-xs text-slate-400 mt-1">
                  💡 L'adresse, le code postal, la ville et le département se rempliront automatiquement
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="postal_code">Code postal *</Label>
                  <Input
                    id="postal_code"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleChange}
                    placeholder="75001"
                    className="h-12"
                    autoComplete="postal-code"
                    data-testid="input-postal"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">Ville *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Paris"
                    className="h-12"
                    autoComplete="address-level2"
                    data-testid="input-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="department">Département *</Label>
                  <Input
                    id="department"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    placeholder="Ex: 75, 69, 13..."
                    maxLength={3}
                    className="h-12"
                    data-testid="input-department"
                  />
                </div>
              </div>

              {/* Type de logement - Cartes cliquables simples */}
              <div className="space-y-4">
                <Label>Type de logement *</Label>
                <div className="flex gap-4">
                  <div 
                    className={`flex-1 p-4 rounded-xl border-2 cursor-pointer transition-all text-center ${
                      formData.housing_type === "maison" 
                        ? 'border-[#1A4D2E] bg-[#E8F5E9]' 
                        : 'border-slate-200 hover:border-[#81B29A]'
                    }`}
                    onClick={() => handleSelectChange("housing_type", "maison")}
                    data-testid="radio-maison"
                  >
                    <span className="font-medium">🏠 Maison</span>
                  </div>
                  <div 
                    className={`flex-1 p-4 rounded-xl border-2 cursor-pointer transition-all text-center ${
                      formData.housing_type === "appartement" 
                        ? 'border-[#1A4D2E] bg-[#E8F5E9]' 
                        : 'border-slate-200 hover:border-[#81B29A]'
                    }`}
                    onClick={() => handleSelectChange("housing_type", "appartement")}
                    data-testid="radio-appartement"
                  >
                    <span className="font-medium">🏢 Appartement</span>
                  </div>
                </div>
              </div>

              {/* Résidence principale ou secondaire - CRUCIAL pour l'éligibilité */}
              <div className="space-y-4">
                <Label>Ce logement est votre *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div 
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.residence_type === "principale" 
                        ? 'border-[#1A4D2E] bg-[#E8F5E9]' 
                        : 'border-slate-200 hover:border-[#81B29A]'
                    }`}
                    onClick={() => handleSelectChange("residence_type", "principale")}
                    data-testid="radio-residence-principale"
                  >
                    <div className="font-medium text-[#1D1D1D]">🏡 Résidence principale</div>
                    <p className="text-xs text-slate-500 mt-1">J'y habite au moins 8 mois par an</p>
                  </div>
                  
                  <div 
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.residence_type === "secondaire" 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-slate-200 hover:border-[#81B29A]'
                    }`}
                    onClick={() => handleSelectChange("residence_type", "secondaire")}
                    data-testid="radio-residence-secondaire"
                  >
                    <div className="font-medium text-[#1D1D1D]">🏖️ Résidence secondaire</div>
                    <p className="text-xs text-slate-500 mt-1">Maison de vacances, pied-à-terre...</p>
                  </div>
                </div>
                
                {/* Avertissement résidence secondaire */}
                {formData.residence_type === "secondaire" && (
                  <div className="bg-orange-50 border border-orange-300 rounded-lg p-4 text-sm">
                    <div className="flex gap-3">
                      <span className="text-orange-600 text-lg">⚠️</span>
                      <div>
                        <p className="font-semibold text-orange-800">Attention : éligibilité réduite</p>
                        <p className="text-orange-700 mt-1">
                          La quasi-totalité des aides (MaPrimeRénov', Prime CEE, Éco-PTZ, MaPrimeAdapt') 
                          sont réservées aux <strong>résidences principales</strong>.
                        </p>
                        <p className="text-orange-700 mt-2">
                          Pour une résidence secondaire, vous pourrez bénéficier :
                        </p>
                        <ul className="text-orange-700 mt-1 ml-4 list-disc">
                          <li>De la TVA à taux réduit (5,5% ou 10%)</li>
                          <li>De certaines aides locales (à vérifier)</li>
                          <li>Du déficit foncier (si location)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <Label>Vous êtes *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Propriétaires */}
                  <div 
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.housing_status === "proprietaire" 
                        ? 'border-[#1A4D2E] bg-[#E8F5E9]' 
                        : 'border-slate-200 hover:border-[#81B29A]'
                    }`}
                    onClick={() => handleSelectChange("housing_status", "proprietaire")}
                    data-testid="radio-proprietaire"
                  >
                    <div className="font-medium text-[#1D1D1D]">🏠 Propriétaire occupant</div>
                    <p className="text-xs text-slate-500 mt-1">J'habite dans mon logement</p>
                  </div>
                  
                  <div 
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.housing_status === "proprietaire_bailleur" 
                        ? 'border-[#1A4D2E] bg-[#E8F5E9]' 
                        : 'border-slate-200 hover:border-[#81B29A]'
                    }`}
                    onClick={() => handleSelectChange("housing_status", "proprietaire_bailleur")}
                    data-testid="radio-bailleur"
                  >
                    <div className="font-medium text-[#1D1D1D]">🔑 Propriétaire bailleur privé</div>
                    <p className="text-xs text-slate-500 mt-1">Je loue mon logement à un particulier</p>
                  </div>
                  
                  <div 
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.housing_status === "bailleur_social" 
                        ? 'border-[#1A4D2E] bg-[#E8F5E9]' 
                        : 'border-slate-200 hover:border-[#81B29A]'
                    }`}
                    onClick={() => handleSelectChange("housing_status", "bailleur_social")}
                    data-testid="radio-bailleur-social"
                  >
                    <div className="font-medium text-[#1D1D1D]">🏢 Bailleur social</div>
                    <p className="text-xs text-slate-500 mt-1">Organisme HLM, OPH, ESH...</p>
                  </div>
                  
                  {/* Locataires */}
                  <div 
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.housing_status === "locataire" 
                        ? 'border-[#1A4D2E] bg-[#E8F5E9]' 
                        : 'border-slate-200 hover:border-[#81B29A]'
                    }`}
                    onClick={() => handleSelectChange("housing_status", "locataire")}
                    data-testid="radio-locataire"
                  >
                    <div className="font-medium text-[#1D1D1D]">📋 Locataire parc privé</div>
                    <p className="text-xs text-slate-500 mt-1">Je loue chez un particulier</p>
                  </div>
                  
                  <div 
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.housing_status === "locataire_social" 
                        ? 'border-[#1A4D2E] bg-[#E8F5E9]' 
                        : 'border-slate-200 hover:border-[#81B29A]'
                    }`}
                    onClick={() => handleSelectChange("housing_status", "locataire_social")}
                    data-testid="radio-locataire-social"
                  >
                    <div className="font-medium text-[#1D1D1D]">🏘️ Locataire parc social</div>
                    <p className="text-xs text-slate-500 mt-1">Je loue un logement social (HLM)</p>
                  </div>
                  
                  <div 
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.housing_status === "usufruitier" 
                        ? 'border-[#1A4D2E] bg-[#E8F5E9]' 
                        : 'border-slate-200 hover:border-[#81B29A]'
                    }`}
                    onClick={() => handleSelectChange("housing_status", "usufruitier")}
                    data-testid="radio-usufruitier"
                  >
                    <div className="font-medium text-[#1D1D1D]">📜 Usufruitier</div>
                    <p className="text-xs text-slate-500 mt-1">J'ai l'usufruit du logement</p>
                  </div>
                </div>
                
                {/* Info MaPrimeAdapt' pour locataires */}
                {(formData.housing_status === "locataire" || formData.housing_status === "locataire_social") && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 mt-2">
                    <p className="font-medium">ℹ️ Locataire et MaPrimeAdapt'</p>
                    <p className="mt-1 text-blue-700">
                      En tant que locataire, vous pouvez bénéficier de MaPrimeAdapt' avec l'accord écrit de votre propriétaire.
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="housing_age">Âge du logement (années) *</Label>
                  <Input
                    id="housing_age"
                    name="housing_age"
                    type="number"
                    value={formData.housing_age}
                    onChange={handleChange}
                    placeholder="20"
                    className="h-12"
                    data-testid="input-housing-age"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="housing_surface">Surface (m²) *</Label>
                  <Input
                    id="housing_surface"
                    name="housing_surface"
                    type="number"
                    value={formData.housing_surface}
                    onChange={handleChange}
                    placeholder="100"
                    className="h-12"
                    data-testid="input-housing-surface"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Work Types - Organisés par catégories */}
          {currentStep === 3 && (
            <div className="space-y-6" data-testid="step-3">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-[#1D1D1D]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Vos travaux souhaités
                </h2>
                <p className="text-slate-500 mt-2">
                  Sélectionnez les travaux que vous envisagez (plusieurs choix possibles)
                </p>
              </div>

              {/* Affichage par catégories */}
              <div className="space-y-6">
                {WORK_CATEGORIES.map((category) => (
                  <div key={category.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    {/* En-tête de catégorie */}
                    <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                      <h3 className="font-semibold text-[#1D1D1D]">{category.title}</h3>
                      <p className="text-xs text-slate-500">{category.description}</p>
                    </div>
                    
                    {/* Liste des travaux de cette catégorie */}
                    <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {category.works.map((work) => {
                        const isSelected = formData.work_types.includes(work.id);
                        return (
                          <div
                            key={work.id}
                            className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                              isSelected
                                ? 'border-[#1A4D2E] bg-[#E8F5E9]'
                                : 'border-slate-200 hover:border-[#81B29A] hover:bg-slate-50'
                            }`}
                            onClick={() => handleWorkTypeToggle(work.id)}
                            data-testid={`work-type-${work.id}`}
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                isSelected 
                                  ? 'bg-[#1A4D2E] border-[#1A4D2E]' 
                                  : 'border-slate-300 bg-white'
                              }`}>
                                {isSelected && <Check className="h-3 w-3 text-white" />}
                              </div>
                              <span className={`text-sm ${isSelected ? 'font-medium text-[#1A4D2E]' : 'text-[#1D1D1D]'}`}>
                                {work.label}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Compteur de travaux sélectionnés */}
              {formData.work_types && formData.work_types.length > 0 && (
                <div className="bg-[#E8F5E9] border border-[#1A4D2E]/20 rounded-xl p-4 text-center">
                  <span className="text-[#1A4D2E] font-medium">
                    {formData.work_types.length} travaux sélectionné{formData.work_types.length > 1 ? 's' : ''}
                  </span>
                </div>
              )}

              {/* Lien vers MaPrimeAdapt' (étape suivante) */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-6">
                <p className="text-blue-800 text-sm">
                  <span className="font-semibold">♿ Adaptation du logement (MaPrimeAdapt')</span><br />
                  <span className="text-blue-700">
                    Si vous ou un membre de votre foyer êtes concerné par une perte d'autonomie, un handicap, ou avez plus de 60 ans, 
                    vous pourrez sélectionner les travaux d'adaptation à l'étape suivante.
                  </span>
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Financial Info */}
          {currentStep === 4 && (
            <div className="space-y-6" data-testid="step-4">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-[#1D1D1D]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Votre situation financière
                </h2>
                <p className="text-slate-500 mt-2">
                  Ces informations déterminent vos droits aux aides
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="income_rfr">Revenu fiscal de référence (€/an) *</Label>
                <Input
                  id="income_rfr"
                  name="income_rfr"
                  type="number"
                  value={formData.income_rfr}
                  onChange={handleChange}
                  placeholder="30000"
                  className="h-12"
                  data-testid="input-income-rfr"
                />
                <p className="text-xs text-slate-400">
                  Indiqué sur votre avis d'imposition (ligne "Revenu fiscal de référence")
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="household_size">Nombre de personnes dans le foyer *</Label>
                <Input
                  id="household_size"
                  name="household_size"
                  type="number"
                  min="1"
                  max="20"
                  value={formData.household_size}
                  onChange={handleChange}
                  placeholder="Ex: 3"
                  className="h-12"
                  data-testid="input-household"
                />
              </div>

              {/* Section MaPrimeAdapt' */}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <div className="flex items-start gap-3 mb-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#1D1D1D]">Adaptation du logement (MaPrimeAdapt')</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Si vous ou un membre de votre foyer êtes concerné par une perte d'autonomie ou un handicap
                    </p>
                  </div>
                </div>

                <div className="space-y-4 bg-slate-50 p-4 rounded-xl">
                  <div className="space-y-2">
                    <Label htmlFor="age">Âge du demandeur (optionnel)</Label>
                    <Input
                      id="age"
                      name="age"
                      type="number"
                      value={formData.age}
                      onChange={handleChange}
                      placeholder="Ex: 72"
                      className="h-12 bg-white"
                      data-testid="input-age"
                    />
                    <p className="text-xs text-slate-400">
                      Si 70 ans ou plus, vous êtes automatiquement éligible à MaPrimeAdapt'
                    </p>
                  </div>

                  <div className="flex flex-col gap-3">
                    <div 
                      className="flex items-center gap-3 cursor-pointer"
                      onClick={() => setFormData(prev => ({ ...prev, situation_handicap: !prev.situation_handicap }))}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        formData.situation_handicap 
                          ? 'bg-[#1A4D2E] border-[#1A4D2E]' 
                          : 'border-slate-300 bg-white'
                      }`}>
                        {formData.situation_handicap && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <span className="text-sm" data-testid="checkbox-handicap">
                        Situation de handicap reconnue (taux ≥ 50%, PCH ou AAH)
                      </span>
                    </div>

                    <div 
                      className="flex items-center gap-3 cursor-pointer"
                      onClick={() => setFormData(prev => ({ ...prev, perte_autonomie: !prev.perte_autonomie }))}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        formData.perte_autonomie 
                          ? 'bg-[#1A4D2E] border-[#1A4D2E]' 
                          : 'border-slate-300 bg-white'
                      }`}>
                        {formData.perte_autonomie && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <span className="text-sm" data-testid="checkbox-autonomie">
                        Perte d'autonomie (APA, GIR 1-6)
                      </span>
                    </div>
                  </div>

                  {/* Niveau GIR - Affiché uniquement si perte d'autonomie cochée */}
                  {formData.perte_autonomie && (
                    <div className="space-y-2 animate-fade-in">
                      <Label htmlFor="gir_level">Niveau GIR (si connu)</Label>
                      <select
                        id="gir_level"
                        value={formData.gir_level || ""}
                        onChange={(e) => handleSelectChange("gir_level", e.target.value)}
                        className="w-full h-12 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white cursor-pointer"
                        style={selectStyle}
                        data-testid="select-gir"
                      >
                        <option value="">Sélectionnez...</option>
                        <option value="inconnu">Je ne sais pas</option>
                        <option value="1">GIR 1 - Dépendance totale</option>
                        <option value="2">GIR 2 - Dépendance sévère</option>
                        <option value="3">GIR 3 - Dépendance modérée</option>
                        <option value="4">GIR 4 - Dépendance légère</option>
                        <option value="5">GIR 5 - Peu dépendant</option>
                        <option value="6">GIR 6 - Autonome</option>
                      </select>
                    </div>
                  )}

                  {/* Travaux d'adaptation - Affichés si âge ≥ 60, handicap OU perte d'autonomie */}
                  {((formData.age && parseInt(formData.age) >= 60) || formData.situation_handicap || formData.perte_autonomie) && (
                    <div className="space-y-3 animate-fade-in">
                      <Label>Types de travaux d'adaptation envisagés</Label>
                      <p className="text-xs text-slate-400 -mt-2">
                        Sélectionnez les travaux qui vous concernent (optionnel mais recommandé pour une estimation précise)
                      </p>
                      <div className="grid grid-cols-1 gap-2">
                        {ADAPTATION_WORK_TYPES.map((work) => {
                          const isSelected = formData.adaptation_works.includes(work.id);
                          const handleToggle = () => {
                            setFormData(prev => ({
                              ...prev,
                              adaptation_works: isSelected 
                                ? prev.adaptation_works.filter(w => w !== work.id)
                                : [...prev.adaptation_works, work.id]
                            }));
                          };
                          return (
                            <div 
                              key={work.id} 
                              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                isSelected
                                  ? 'border-[#1A4D2E] bg-[#E8F5E9]'
                                  : 'border-slate-200 bg-white hover:border-[#81B29A]'
                              }`}
                              onClick={handleToggle}
                            >
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                isSelected 
                                  ? 'bg-[#1A4D2E] border-[#1A4D2E]' 
                                  : 'border-slate-300 bg-white'
                              }`}>
                                {isSelected && <Check className="h-3 w-3 text-white" />}
                              </div>
                              <span className="text-sm flex-1" data-testid={`adapt-${work.id}`}>
                                {work.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Message informatif si la section MaPrimeAdapt' est activée */}
                  {((formData.age && parseInt(formData.age) >= 60) || formData.situation_handicap || formData.perte_autonomie) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                      <p className="font-medium">ℹ️ À propos de MaPrimeAdapt'</p>
                      <p className="mt-1 text-blue-700">
                        Ces champs sont optionnels. Si vous ne les remplissez pas entièrement, nous pourrons quand même estimer votre éligibilité (statut "à confirmer").
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Section Loc'Avantages (propriétaire bailleur uniquement) */}
              {formData.housing_status === "proprietaire_bailleur" && (
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-[#1D1D1D]">Loc'Avantages (réduction d'impôt)</h3>
                      <p className="text-sm text-slate-500 mt-1">
                        Bénéficiez d'une réduction d'impôt en louant à loyer maîtrisé
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4 bg-amber-50 p-4 rounded-xl border border-amber-100">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="owner_type">Type de propriétaire</Label>
                        <select
                          id="owner_type"
                          value={formData.owner_type || "personne_physique"}
                          onChange={(e) => handleSelectChange("owner_type", e.target.value)}
                          className="w-full h-12 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white cursor-pointer"
                          style={selectStyle}
                          data-testid="select-owner-type"
                        >
                          <option value="personne_physique">Personne physique</option>
                          <option value="sci_familiale">SCI familiale</option>
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="zone_tendue">Zone de tension locative</Label>
                        <select
                          id="zone_tendue"
                          value={formData.zone_tendue || ""}
                          onChange={(e) => handleSelectChange("zone_tendue", e.target.value)}
                          className="w-full h-12 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white cursor-pointer"
                          style={selectStyle}
                          data-testid="select-zone"
                        >
                          <option value="">Sélectionner...</option>
                          <option value="a_bis">Zone A bis (Paris)</option>
                          <option value="a">Zone A (Grandes métropoles)</option>
                          <option value="b1">Zone B1 (Villes moyennes)</option>
                          <option value="b2">Zone B2 (Autres agglomérations)</option>
                          <option value="c">Zone C (Zones rurales)</option>
                        </select>
                        <p className="text-xs text-amber-700">
                          <a href="https://www.service-public.fr/simulateur/calcul/zonage-abc" target="_blank" rel="noopener noreferrer" className="underline">
                            Vérifier ma zone
                          </a>
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Niveau de conventionnement souhaité</Label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {[
                          { value: "loc1", label: "Loc1 - Intermédiaire", desc: "-15% du marché", reduction: "15-20%" },
                          { value: "loc2", label: "Loc2 - Social", desc: "-30% du marché", reduction: "35-40%" },
                          { value: "loc3", label: "Loc3 - Très social", desc: "-45% du marché", reduction: "65%" }
                        ].map(option => (
                          <div 
                            key={option.value}
                            className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              formData.conventionnement_level === option.value
                                ? 'border-amber-500 bg-amber-100'
                                : 'border-amber-200 bg-white hover:border-amber-300'
                            }`}
                            onClick={() => handleSelectChange("conventionnement_level", option.value)}
                            data-testid={`conv-${option.value}`}
                          >
                            <p className="font-medium text-sm">{option.label}</p>
                            <p className="text-xs text-amber-700">{option.desc}</p>
                            <p className="text-xs font-semibold text-amber-800 mt-1">Réduction: {option.reduction}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="market_rent">Loyer de marché estimé (€/mois)</Label>
                        <Input
                          id="market_rent"
                          name="market_rent"
                          type="number"
                          value={formData.market_rent}
                          onChange={handleChange}
                          placeholder="Ex: 1000"
                          className="h-12 bg-white"
                          data-testid="input-market-rent"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="rental_duration_years">Durée d'engagement</Label>
                        <select
                          id="rental_duration_years"
                          value={String(formData.rental_duration_years || 6)}
                          onChange={(e) => handleSelectChange("rental_duration_years", parseInt(e.target.value))}
                          className="w-full h-12 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white cursor-pointer"
                          style={selectStyle}
                          data-testid="select-duration"
                        >
                          <option value="6">6 ans (minimum)</option>
                          <option value="9">9 ans</option>
                          <option value="12">12 ans</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3">
                      <div 
                        className="flex items-center gap-3 cursor-pointer"
                        onClick={() => setFormData(prev => ({ ...prev, with_intermediation: !prev.with_intermediation }))}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          formData.with_intermediation 
                            ? 'bg-[#1A4D2E] border-[#1A4D2E]' 
                            : 'border-slate-300 bg-white'
                        }`}>
                          {formData.with_intermediation && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <span className="text-sm" data-testid="checkbox-intermediation">
                          Avec intermédiation locative (gestion par organisme agréé = +5% de réduction)
                        </span>
                      </div>

                      <div 
                        className="flex items-center gap-3 cursor-pointer"
                        onClick={() => setFormData(prev => ({ ...prev, has_travaux_renovation: !prev.has_travaux_renovation }))}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          formData.has_travaux_renovation 
                            ? 'bg-[#1A4D2E] border-[#1A4D2E]' 
                            : 'border-slate-300 bg-white'
                        }`}>
                          {formData.has_travaux_renovation && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <span className="text-sm" data-testid="checkbox-travaux">
                          Travaux de rénovation prévus (cumul possible avec MaPrimeRénov')
                        </span>
                      </div>
                    </div>

                    <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 text-sm text-amber-900">
                      <p className="font-medium">💡 Bon à savoir</p>
                      <p className="mt-1">
                        La réduction d'impôt s'applique sur le montant total des loyers perçus. 
                        Le logement doit avoir un DPE au moins F depuis 2025.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <Label>Quand souhaitez-vous réaliser vos travaux ? *</Label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: "immediat", label: "⚡ Immédiatement" },
                    { value: "3_mois", label: "📅 Dans 3 mois" },
                    { value: "6_mois", label: "📆 Dans 6 mois" },
                    { value: "plus_tard", label: "⏰ Plus tard" },
                  ].map((option) => (
                    <div 
                      key={option.value}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all text-center ${
                        formData.urgency === option.value
                          ? 'border-[#1A4D2E] bg-[#E8F5E9]'
                          : 'border-slate-200 hover:border-[#81B29A]'
                      }`}
                      onClick={() => handleSelectChange("urgency", option.value)}
                      data-testid={`urgency-${option.value}`}
                    >
                      <span className="font-medium text-[#1D1D1D]">{option.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="additional_info">Informations complémentaires (optionnel)</Label>
                <Textarea
                  id="additional_info"
                  name="additional_info"
                  value={formData.additional_info}
                  onChange={handleChange}
                  placeholder="Précisez ici toute information utile..."
                  className="min-h-[100px]"
                  data-testid="input-additional-info"
                />
              </div>
            </div>
          )}

          {/* Step 5: Summary */}
          {currentStep === 5 && (
            <div className="space-y-6" data-testid="step-5">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-[#1D1D1D]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Récapitulatif
                </h2>
                <p className="text-slate-500 mt-2">
                  Vérifiez vos informations avant de valider
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-semibold text-[#1A4D2E] mb-3">Informations personnelles</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p className="text-slate-500">Nom:</p>
                    <p className="font-medium">{formData.first_name} {formData.last_name}</p>
                    <p className="text-slate-500">Email:</p>
                    <p className="font-medium">{formData.email}</p>
                    <p className="text-slate-500">Téléphone:</p>
                    <p className="font-medium">{formData.phone}</p>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-semibold text-[#1A4D2E] mb-3">Logement</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p className="text-slate-500">Adresse:</p>
                    <p className="font-medium">{formData.address}, {formData.postal_code} {formData.city}</p>
                    <p className="text-slate-500">Type:</p>
                    <p className="font-medium capitalize">{formData.housing_type}</p>
                    <p className="text-slate-500">Résidence:</p>
                    <p className={`font-medium ${formData.residence_type === "secondaire" ? "text-orange-600" : ""}`}>
                      {formData.residence_type === "principale" ? "Principale" : "Secondaire"}
                    </p>
                    <p className="text-slate-500">Statut:</p>
                    <p className="font-medium">{getHousingStatusLabel(formData.housing_status)}</p>
                    <p className="text-slate-500">Surface:</p>
                    <p className="font-medium">{formData.housing_surface} m²</p>
                    <p className="text-slate-500">Âge:</p>
                    <p className="font-medium">{formData.housing_age} ans</p>
                  </div>
                  {formData.residence_type === "secondaire" && (
                    <div className="mt-3 p-2 bg-orange-100 rounded text-xs text-orange-700">
                      ⚠️ Résidence secondaire : éligibilité limitée aux aides
                    </div>
                  )}
                </div>

                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-semibold text-[#1A4D2E] mb-3">Travaux souhaités</h3>
                  <div className="flex flex-wrap gap-2">
                    {formData.work_types.map(type => (
                      <span key={type} className="px-3 py-1 bg-[#E8F5E9] text-[#1A4D2E] rounded-full text-sm">
                        {WORK_TYPES.find(w => w.id === type)?.label}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-semibold text-[#1A4D2E] mb-3">Situation financière</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p className="text-slate-500">Revenu fiscal:</p>
                    <p className="font-medium">{parseInt(formData.income_rfr).toLocaleString('fr-FR')} €/an</p>
                    <p className="text-slate-500">Foyer:</p>
                    <p className="font-medium">{formData.household_size} personne(s)</p>
                    <p className="text-slate-500">Échéance:</p>
                    <p className="font-medium capitalize">{formData.urgency.replace('_', ' ')}</p>
                  </div>
                </div>

                {/* Section MaPrimeAdapt' dans le récapitulatif */}
                {((formData.age && parseInt(formData.age) >= 60) || formData.situation_handicap || formData.perte_autonomie) && (
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                    <h3 className="font-semibold text-blue-800 mb-3">🏠 Adaptation du logement (MaPrimeAdapt')</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {formData.age && (
                        <>
                          <p className="text-blue-600">Âge:</p>
                          <p className="font-medium text-blue-900">{formData.age} ans</p>
                        </>
                      )}
                      {formData.situation_handicap && (
                        <>
                          <p className="text-blue-600">Handicap:</p>
                          <p className="font-medium text-blue-900">Oui (reconnu)</p>
                        </>
                      )}
                      {formData.perte_autonomie && (
                        <>
                          <p className="text-blue-600">Perte d'autonomie:</p>
                          <p className="font-medium text-blue-900">Oui{formData.gir_level && formData.gir_level !== "inconnu" ? ` (GIR ${formData.gir_level})` : ''}</p>
                        </>
                      )}
                    </div>
                    {formData.adaptation_works.length > 0 && (
                      <div className="mt-3">
                        <p className="text-blue-600 text-sm mb-2">Travaux d'adaptation envisagés:</p>
                        <div className="flex flex-wrap gap-2">
                          {formData.adaptation_works.map(workId => (
                            <span key={workId} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                              {ADAPTATION_WORK_TYPES.find(w => w.id === workId)?.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-10 pt-6 border-t border-slate-100">
            {currentStep > 1 ? (
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                className="h-12 px-6 border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white"
                data-testid="btn-prev"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Précédent
              </Button>
            ) : (
              <div />
            )}

            {currentStep < STEPS.length ? (
              <Button
                type="button"
                onClick={nextStep}
                className="h-12 px-8 bg-[#1A4D2E] hover:bg-[#143d24] text-white btn-shine"
                data-testid="btn-next"
              >
                Suivant
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                type="submit"
                onClick={handleSubmit}
                disabled={loading}
                className="h-12 px-8 bg-[#E07A5F] hover:bg-[#cc6b52] text-white btn-shine"
                data-testid="btn-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                    Envoi...
                  </span>
                ) : (
                  <>
                    {isEditMode ? "Recalculer mon éligibilité" : "Vérifier mon éligibilité"}
                    <CheckCircle className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </form>
      </main>
    </div>
  );
}
